/**
 * Shared notification types + helpers (feature 41).
 *
 * The Notifications page, the header bell, and the dashboard "top notifications"
 * all read the same self-scoped feed (GET /api/my-notifications) and render rows
 * the same way — so the row shape, the read/unread rule, the per-trigger
 * icon/label, the entity deep-link map, and the relative-time formatter live here
 * once instead of being duplicated across the three surfaces.
 */

import { formatDate } from './orgTimezone';

export interface Notification {
  id: string;
  trigger_type: string | null;
  urgency: 'low' | 'normal' | 'high' | 'critical' | string | null;
  title: string | null;
  body: string | null;
  entity_type: string | null;
  entity_id: string | null;
  status: string | null;
  created_at: string | null;
  /** Per-recipient flags from /api/my-notifications (notification_recipients). */
  // Per-recipient read flag. The pg wrapper serializes BOOLEAN columns as 0/1,
  // so this arrives as a number at runtime (not a JS boolean) — see isUnread.
  read?: boolean | number | null;
  read_at?: string | null;
}

export type BadgeVariant = 'success' | 'warning' | 'danger' | 'info' | 'neutral';

export const urgencyVariant: Record<string, BadgeVariant> = {
  low: 'neutral',
  normal: 'info',
  high: 'warning',
  critical: 'danger',
};

export const urgencyLabel: Record<string, string> = {
  low: 'Low',
  normal: 'Normal',
  high: 'High',
  critical: 'Critical',
};

// Portal client-action trigger types — the "we know what the client did" feature.
export const PORTAL_TRIGGERS = new Set([
  'client_document_viewed',
  'client_document_uploaded',
  'client_comment_added',
]);

// Human label per trigger_type. Covers the portal events, the legacy
// deadline/status triggers, AND the feature-41 matrix event types. The per-type
// GLYPH lives in organisms/NotificationTypeIcon (a clean stroke line icon keyed
// off the same trigger_type) — this map is label-only.
export const triggerMeta: Record<string, { label: string }> = {
  client_document_viewed: { label: 'Client viewed a document' },
  client_document_uploaded: { label: 'Client uploaded a document' },
  client_comment_added: { label: 'Client posted a comment' },
  firm_comment_added: { label: 'Reply sent to client' },
  deadline_approaching: { label: 'Deadline approaching' },
  status_change: { label: 'Status change' },
  note_mention: { label: 'You were mentioned' },
  // ── feature-41 matrix events ──
  appeal_filed: { label: 'Appeal filed' },
  appeal_stage_advanced: { label: 'Appeal stage advanced' },
  appeal_outcome: { label: 'Appeal outcome recorded' },
  bill_status_changed: { label: 'Tax-bill status changed' },
  invoice_status_changed: { label: 'Invoice sent' },
  invoice_paid: { label: 'Invoice paid' },
  document_uploaded: { label: 'Document uploaded' },
  document_shared: { label: 'Document shared' },
  document_unshared: { label: 'Document unshared' },
  client_created: { label: 'Client onboarded' },
  property_created: { label: 'Property added' },
  parcel_created: { label: 'Parcel added' },
};

export function humanize(value: string | null): string {
  if (!value) return 'Notification';
  return value.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

export function metaFor(triggerType: string | null): { label: string } {
  if (triggerType && triggerMeta[triggerType]) return triggerMeta[triggerType];
  return { label: humanize(triggerType) };
}

// A notification counts as "unread" unless the recipient flag says otherwise
// (per-recipient `read`), falling back to the notification-level status.
const READ_STATUSES = new Set(['read', 'dismissed', 'archived', 'resolved']);
export function isUnread(n: Pick<Notification, 'read' | 'status'>): boolean {
  // `read` is the authoritative per-recipient flag (notification_recipients.read),
  // updated by mark-read / mark-all-read. It arrives as 0/1 (pg BOOLEAN → number)
  // OR a boolean, so accept both: !0 = unread, !1 = read. Only fall back to the
  // shared `status` when there is no per-recipient flag at all.
  if (n.read != null) return !n.read;
  return !n.status || !READ_STATUSES.has(n.status);
}

// entity_type may arrive as a table name ('documents') or singular ('document').
// Map to the v2 route segment; return undefined when there's no detail page.
export const entityRoute: Record<string, string> = {
  documents: 'documents',
  document: 'documents',
  clients: 'clients',
  client: 'clients',
  parcels: 'parcels',
  parcel: 'parcels',
  properties: 'properties',
  property: 'properties',
  invoices: 'invoices',
  invoice: 'invoices',
  tax_bills: 'tax-bills',
  tax_bill: 'tax-bills',
};

export function routeFor(n: Pick<Notification, 'entity_type' | 'entity_id' | 'trigger_type'>): string | null {
  const seg = n.entity_type ? entityRoute[n.entity_type] : undefined;
  if (!seg || !n.entity_id) return null;
  const base = `/${seg}/${n.entity_id}`;
  // A mention lands the user on the record's Notes tab (where the note lives),
  // not the profile overview.
  return n.trigger_type === 'note_mention' ? `${base}?tab=notes` : base;
}

export function relativeTime(iso: string | null): string {
  if (!iso) return '—';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '—';
  const sec = Math.round((Date.now() - then) / 1000);
  if (sec < 45) return 'just now';
  const min = Math.round(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.round(hr / 24);
  if (day < 30) return `${day}d ago`;
  return formatDate(iso, { dateOnly: true });
}
